import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["track", "range", "handleMin", "handleMax", "inputMin", "inputMax", "rangeDisplay"];
  
  static values = {
    minLimit: Number,
    maxLimit: Number,
    step: Number,
    filterParam: String
  };
  
  static outlets = ["filters"];

  connect() {
    // Bind methods to this instance
    this.boundDrag = this.drag.bind(this);
    this.boundStopDrag = this.stopDrag.bind(this);
    
    // Add global event listeners for mouse and touch events
    document.addEventListener('mousemove', this.boundDrag);
    document.addEventListener('touchmove', this.boundDrag);
    document.addEventListener('mouseup', this.boundStopDrag); 
    document.addEventListener('touchend', this.boundStopDrag);

    this.loadFromUrlParams();
    
    // Wait for DOM to be ready and visible
    this.initializationTimer = setInterval(() => {
      // Only initialize if the track has a width (visible in DOM)
      if (this.trackTarget.offsetWidth > 0) {
        // Clear the interval once we have valid dimensions
        clearInterval(this.initializationTimer);
        
        this.setSliderConfiguration();
        this.initializeInputFields();
        this.initializeSlider();
        
        console.log('Range slider initialized with:', {
          minLimit: this.minLimitValue,
          maxLimit: this.maxLimitValue,
          inputMin: this.inputMinTarget.value,
          inputMax: this.inputMaxTarget.value
        });
      }
    }, 100);
  }
  
  /**
   * Get configuration values from data attributes
   * Sets up min, max, and step values for the slider
   */
  setSliderConfiguration() {
    this.trackWidth = this.trackTarget.offsetWidth;
    this.handleWidth = this.handleMinTarget.offsetWidth;
    this.filterParam = this.element.dataset.rangeFilterFilterParam;
    this.formatType = this.element.dataset.rangeFilterFormatType || 'number';

    this.minLimitValue = parseInt(this.element.dataset.rangeFilterMinValue) || 0;
    this.maxLimitValue = parseInt(this.element.dataset.rangeFilterMaxValue) || 100;
    
    if (this.element.dataset.rangeFilterStep) {
      this.stepValue = parseInt(this.element.dataset.rangeFilterStep) || 1;
    } else {
      const range = this.maxLimitValue - this.minLimitValue;
      this.stepValue = Math.max(1, Math.floor(range / 100));
    }
  }

  /**
   * Initialize input fields with min/max attributes and validate input values
   * Ensures input values are within valid ranges and min <= max
   */
  initializeInputFields() {
    // Set min/max attributes on input fields
    // This ensures the browser's built-in validation works
    this.inputMinTarget.min = this.minLimitValue;
    this.inputMinTarget.max = this.maxLimitValue;
    this.inputMaxTarget.min = this.minLimitValue;
    this.inputMaxTarget.max = this.maxLimitValue;
    
    let minVal = parseInt(this.inputMinTarget.value) || this.minLimitValue;
    let maxVal = parseInt(this.inputMaxTarget.value) || this.maxLimitValue;
    
    // Ensure min <= max and values are within valid ranges
    minVal = Math.max(this.minLimitValue, Math.min(minVal, maxVal));
    maxVal = Math.max(minVal, Math.min(maxVal, this.maxLimitValue));
    
    this.inputMinTarget.value = minVal;
    this.inputMaxTarget.value = maxVal;
  }


  /**
   * Initialize the slider UI based on current input values
   * Sets handle positions and updates the range bar
   */
  initializeSlider() {
    const minVal = parseInt(this.inputMinTarget.value);
    const maxVal = parseInt(this.inputMaxTarget.value);
    
    const minPos = this.valueToPosition(minVal);
    const maxPos = this.valueToPosition(maxVal);
    
    this.setHandlePosition(this.handleMinTarget, Math.min(minPos, this.trackWidth - this.handleWidth));
    this.setHandlePosition(this.handleMaxTarget, Math.min(maxPos, this.trackWidth - this.handleWidth));
    
    this.updateRangeBar();
  }

  disconnect() {
    document.removeEventListener('mousemove', this.boundDragMin);
    document.removeEventListener('mousemove', this.boundDragMax);
    document.removeEventListener('mouseup', this.boundStopDrag);
    document.removeEventListener('touchmove', this.boundDragMin);
    document.removeEventListener('touchmove', this.boundDragMax);
    document.removeEventListener('touchend', this.boundStopDrag);
    
    if (this.initializationTimer) {
      clearInterval(this.initializationTimer);
    }
  }
  
  /**
   * Start dragging a handle (min or max)
   * @param {Event} event - The mousedown or touchstart event
   */
  startDrag(event) {
    event.preventDefault();
    
    this.draggingMin = (event.currentTarget === this.handleMinTarget)
    this.draggingMax = (event.currentTarget === this.handleMaxTarget)
    
    if (event.type === 'touchstart') {
      this.initialClientX = event.touches[0].clientX;
    }
  }
  
  /**
   * Handle dragging of either min or max handle
   * @param {Event} event - The mouse or touch event
   */
  drag(event) {
    if (!this.draggingMin && !this.draggingMax) return;
    
    const handleTarget = this.draggingMin ? this.handleMinTarget : this.handleMaxTarget;
    const inputTarget = this.draggingMin ? this.inputMinTarget : this.inputMaxTarget;
    
    // Get client X position, handling both mouse and touch events
    const clientX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX;
    
    // Get track bounds
    const trackRect = this.trackTarget.getBoundingClientRect();
    let position = clientX - trackRect.left;
    
    // Check if max handle is near the right edge (within 5px) before constraining
    const isNearRightEdge = this.draggingMax && (position >= trackRect.width - 5);
    
    // Constrain to track bounds
    const maxAllowedPosition = this.trackWidth - this.handleWidth;
    position = Math.max(0, Math.min(position, maxAllowedPosition));
    
    if (this.draggingMin) {
      // Min handle: Don't exceed max handle
      const maxHandlePosition = parseFloat(this.handleMaxTarget.style.left) || this.trackWidth;
      position = Math.min(position, maxHandlePosition - this.handleWidth);
    } else if (this.draggingMax) {
      // Max handle: Don't go below min handle
      const minHandlePosition = parseFloat(this.handleMinTarget.style.left) || 0;
      position = Math.max(position, minHandlePosition + this.handleWidth);
    }
    
    this.setHandlePosition(handleTarget, position);
    this.updateRangeBar();
    
    let value = (isNearRightEdge) ? this.maxLimitValue : this.positionToValue(position);
    inputTarget.value = value;
    
    // Update display values
    if (this.draggingMin) {
      this.updateDisplayValues(value, parseInt(this.inputMaxTarget.value));
    } else if (this.draggingMax) {
      this.updateDisplayValues(parseInt(this.inputMinTarget.value), value);
    }
    
    this.updateFilter();
  }
  
  stopDrag() {
    if (this.draggingMin || this.draggingMax) {
      this.draggingMin = false;
      this.draggingMax = false;
      
      const minValue = parseInt(this.inputMinTarget.value);
      const maxValue = parseInt(this.inputMaxTarget.value);
      this.updateDisplayValues(minValue, maxValue);
      
      this.updateFilter();
    }
  }
  
  // Update the display values
  updateDisplayValues(minValue, maxValue) {
    if (this.hasRangeDisplayTarget) {
      let formattedMin, formattedMax;
      
      switch (this.formatType) {
        case 'currency':
          formattedMin = this.formatCurrency(minValue);
          formattedMax = this.formatCurrency(maxValue);
          break;
        case 'percentage':
          formattedMin = `${minValue}%`;
          formattedMax = `${maxValue}%`;
          break;
        case 'number':
        default:
          formattedMin = minValue;
          formattedMax = maxValue;
          break;
      }
      
      this.rangeDisplayTarget.textContent = `${formattedMin} - ${formattedMax}`;
    }
  }
  
  /**
   * Handle input changes from the number inputs
   * Updates the slider position based on input values and notifies the filters controller
   * @param {Event} event - The change event
   */
  handleInputChange(event) {
    // Validate and normalize input values
    let minValue = parseInt(this.inputMinTarget.value) || this.minLimitValue;
    let maxValue = parseInt(this.inputMaxTarget.value) || this.maxLimitValue;
    
    const minLimit = this.minLimitValue;
    const maxLimit = this.maxLimitValue;
    
    minValue = Math.max(minLimit, Math.min(minValue, maxLimit));
    maxValue = Math.max(minValue, Math.min(maxValue, maxLimit));
    
    this.inputMinTarget.value = minValue;
    this.inputMaxTarget.value = maxValue;
    
    if (event.target === this.inputMinTarget) {
      const position = this.valueToPosition(minValue);
      this.setHandlePosition(this.handleMinTarget, position);
    } else if (event.target === this.inputMaxTarget) {
      const position = this.valueToPosition(maxValue);
      this.setHandlePosition(this.handleMaxTarget, position);
    }
    
    this.updateDisplayValues(minValue, maxValue);
    this.updateRangeBar();
    
    this.updateFilter();
  }
  
  /**
   * Update filter with current range values
   * Sends the current range values to the filters controller
   * If values match the min/max limits, passes null to indicate no filter
   */
  updateFilter(initializing = false) {
    let minValue = this.inputMinTarget.value.trim();
    let maxValue = this.inputMaxTarget.value.trim();
    
    // If values match the limits, pass null to indicate no filter
    if (minValue === this.minLimitValue.toString()) {
      minValue = null;
    }
    
    if (maxValue === this.maxLimitValue.toString()) {
      maxValue = null;
    }
    
    this.filtersOutlet.updateRangeFilter(this.filterParam, minValue, maxValue, initializing);
  }
  
  /**
   * Clear the range filter and reset to default values
   */
  clear() {
    // Reset input values to min/max limits
    this.inputMinTarget.value = this.minLimitValue;
    this.inputMaxTarget.value = this.maxLimitValue;
    
    // Reset slider positions
    const minPos = this.valueToPosition(this.minLimitValue);
    const maxPos = this.valueToPosition(this.maxLimitValue);
    
    this.setHandlePosition(this.handleMinTarget, minPos);
    this.setHandlePosition(this.handleMaxTarget, maxPos);
    
    // Update display and range bar
    this.updateDisplayValues(this.minLimitValue, this.maxLimitValue);
    this.updateRangeBar();
    
    // Update filter
    this.updateFilter();
  }
  
  /**
   * Load range values from URL parameters
   * This ensures the slider reflects any filter values in the URL
   */
  loadFromUrlParams() {
    this.filterParam = this.element.dataset.rangeFilterFilterParam;
    this.formatType = this.element.dataset.rangeFilterFormatType || 'number';

    const params = new URLSearchParams(window.location.search);
    const minParamName = `${this.filterParam}_min`;
    const maxParamName = `${this.filterParam}_max`;
    
    const minValue = params.get(minParamName);
    const maxValue = params.get(maxParamName);
    
    if (minValue) {
      this.inputMinTarget.value = minValue;
    }
    
    if (maxValue) {
      this.inputMaxTarget.value = maxValue;
    }
    
    if (minValue || maxValue) {
      this.updateSlider();
      this.updateFilter(true);
    }
  }
  
  /**
   * Convert a value to a position on the slider track
   * @param {number} value - The value to convert
   * @returns {number} - The position in pixels
   */
  valueToPosition(value) {
    value = parseFloat(value) || this.minLimitValue || 0;
    
    const trackWidth = this.trackWidth || 100;
    const handleWidth = this.handleWidth || 0;
    const availableWidth = trackWidth - handleWidth;
    
    const minValue = this.minLimitValue || 0;
    const maxValue = this.maxLimitValue || 100;
    
    // Special case: If value is at or above max value, position at the end of the track
    if (value >= maxValue) {
      return availableWidth;
    }
    
    // Calculate percentage of value within range (constrained to 0-1)
    const percentage = maxValue > minValue ? 
      Math.max(0, Math.min(1, (value - minValue) / (maxValue - minValue))) : 0;
    
    return percentage * availableWidth;
  }
  
  /**
   * Convert a position on the slider track to a value
   * @param {number} position - The position in pixels
   * @returns {number} - The calculated value
   */
  positionToValue(position) {
    position = parseFloat(position) || 0;
    
    const trackWidth = this.trackWidth || 100;
    const handleWidth = this.handleWidth || 0;
    const availableWidth = trackWidth - handleWidth;
    
    const minValue = this.minLimitValue || 0;
    const maxValue = this.maxLimitValue || 100;
    const stepValue = this.stepValue || 1;    
    // Special case: If position is very close to the max (within 5px or 5% of track width),
    // return the exact max value to ensure rightmost drag works correctly
    const threshold = Math.min(5, trackWidth * 0.05);
    if (position >= availableWidth - threshold) {
      return maxValue;
    }
    
    position = Math.max(0, Math.min(position, availableWidth));
    
    const percentage = position / availableWidth;
    let value = minValue + percentage * (maxValue - minValue);
    
    value = Math.round(value / stepValue) * stepValue;
    return value;
  }
  
  setHandlePosition(handle, position) {
    if (isNaN(position)) {
      console.error('Invalid position value:', position);
      position = 0;
    }
    
    handle.style.left = `${position}px`;
  }
  
  updateRangeBar() {
    let minHandlePosition = parseFloat(this.handleMinTarget.style.left);
    let maxHandlePosition = parseFloat(this.handleMaxTarget.style.left);
      
    if (isNaN(minHandlePosition)) minHandlePosition = 0;
    if (isNaN(maxHandlePosition)) {
      maxHandlePosition = (this.trackWidth || 100) - (this.handleWidth || 0);
    }
      
    minHandlePosition = Math.max(0, Math.min(minHandlePosition, this.trackWidth || 100));
    maxHandlePosition = Math.max(0, Math.min(maxHandlePosition, this.trackWidth || 100));
      
    if (minHandlePosition > maxHandlePosition) {
      minHandlePosition = maxHandlePosition;
    }
      
    const width = Math.max(0, maxHandlePosition - minHandlePosition);
    this.rangeTarget.style.left = `${minHandlePosition}px`;
    this.rangeTarget.style.width = `${width}px`;
  }
  
  updateSlider() {
    const minValue = parseInt(this.inputMinTarget.value) || this.minLimitValue;
    const maxValue = parseInt(this.inputMaxTarget.value) || this.maxLimitValue;
    
    const minPosition = this.valueToPosition(minValue);
    const maxPosition = this.valueToPosition(maxValue);
      
    this.setHandlePosition(this.handleMinTarget, minPosition);
    this.setHandlePosition(this.handleMaxTarget, maxPosition);
      
    this.updateRangeBar();    
    this.updateDisplayValues(minValue, maxValue);
  }
  
  formatCurrency(value) {
    value = parseFloat(value);
    if (isNaN(value)) {
      console.error('Invalid value for currency formatting:', value);
      return '$0';
    }
    
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    return formatter.format(value);
  }
}
